// Laveston — Rewards Progress Bar (2-in-1) + unlock confetti
// Track (fill + dots): stacking discount by item count (2/3/4/5 items -> 10/15/20/30%).
// Status line: free shipping by eligible cart total ($70).
// Confetti fires ONCE per new unlock. Baseline (`seen`) is module-scoped so it
// survives cart-drawer re-renders and never fires on a plain page load.
// Confetti is shown in the TOP LAYER (Popover API) so it paints ABOVE the modal
// cart drawer; falls back to rendering inside the open drawer on older browsers.
// Mechanic unchanged: /cart.js is the single source of truth, polled every POLL_MS + cart events.

(function () {
  if (customElements.get('laveston-rewards-bar')) return;

  const FREE_SHIP = 7000;   // $70 in cents
  const POLL_MS   = 1500;
  const GIFT_PROP = '_gift';

  const TIERS = [
    { items: 2, label: '10%' },
    { items: 3, label: '15%' },
    { items: 4, label: '20%' },
    { items: 5, label: '30%' }
  ];
  const MAX_ITEMS = TIERS[TIERS.length - 1].items;

  let lastCart = null;
  let seen = null; // { tier, ship } — module-scoped so it persists across re-renders

  const money = (cents) => {
    const d = cents / 100;
    return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
  };

  // ---- Full-page colorful confetti, rendered in the top layer (above the modal drawer) ----
  if (!window.lavestonConfetti) {
    window.lavestonConfetti = function () {
      const COLORS = ['#E63946', '#F4A261', '#FFD166', '#2A9D8F', '#457B9D', '#E76F51', '#B5179E', '#8A2330'];

      const host = document.createElement('div');
      host.setAttribute('aria-hidden', 'true');
      host.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;margin:0;padding:0;border:0;background:transparent;pointer-events:none;overflow:visible;z-index:2147483647;';

      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'width:100%;height:100%;display:block;pointer-events:none;';
      host.appendChild(canvas);

      // Prefer the Popover API so the canvas enters the top layer AFTER the drawer
      // dialog, stacking above it. pointer-events:none keeps checkout clickable.
      const canPopover = ('popover' in HTMLElement.prototype) && (typeof host.showPopover === 'function');
      let mounted = false;
      if (canPopover) {
        host.setAttribute('popover', 'manual');
        document.body.appendChild(host);
        try { host.showPopover(); mounted = true; } catch (_) { mounted = false; }
      }
      if (!mounted) {
        // Fallback: render inside the open modal drawer (shares its top layer).
        // On mobile the drawer is full-screen, so this still covers the view.
        const openDialog = document.querySelector('dialog[open]');
        host.style.position = openDialog ? 'absolute' : 'fixed';
        (openDialog || document.body).appendChild(host);
      }

      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const W = () => host.clientWidth || window.innerWidth;
      const H = () => host.clientHeight || window.innerHeight;
      let vw = W(), vh = H();
      canvas.width = vw * dpr; canvas.height = vh * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const N = 170;
      const parts = [];
      for (let i = 0; i < N; i++) {
        parts.push({
          x: Math.random() * vw,
          y: -20 - Math.random() * vh * 0.4,
          w: 6 + Math.random() * 6,
          h: 8 + Math.random() * 9,
          color: COLORS[(Math.random() * COLORS.length) | 0],
          vx: (Math.random() - 0.5) * 3,
          vy: 3 + Math.random() * 4.5,
          rot: Math.random() * Math.PI,
          vr: (Math.random() - 0.5) * 0.3,
          sway: Math.random() * 6.28
        });
      }

      const cleanup = () => {
        try { if (host.matches && host.matches(':popover-open') && host.hidePopover) host.hidePopover(); } catch (_) {}
        host.remove();
      };

      const start = performance.now();
      const DURATION = 2800;
      function frame(now) {
        const t = now - start;
        ctx.clearRect(0, 0, vw, vh);
        let alive = false;
        for (const p of parts) {
          p.vy += 0.05;
          p.x += p.vx + Math.sin((t / 320) + p.sway) * 0.7;
          p.y += p.vy;
          p.rot += p.vr;
          const fade = t > DURATION - 700 ? Math.max(0, (DURATION - t) / 700) : 1;
          if (p.y < vh + 40 && fade > 0) alive = true;
          ctx.save();
          ctx.globalAlpha = fade;
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          ctx.restore();
        }
        if (t < DURATION && alive) requestAnimationFrame(frame);
        else cleanup();
      }
      requestAnimationFrame(frame);
    };
  }

  class LavestonRewardsBar extends HTMLElement {
    constructor() {
      super();
      this.pollId = null;
    }

    connectedCallback() {
      if (lastCart) this.render(lastCart);
      this.refresh();
      this.startPolling();
      ['cart:update', 'cart:refresh', 'cart:added', 'cart:change'].forEach((e) =>
        document.addEventListener(e, this.handleCartEvent)
      );
    }

    disconnectedCallback() {
      this.stopPolling();
      ['cart:update', 'cart:refresh', 'cart:added', 'cart:change'].forEach((e) =>
        document.removeEventListener(e, this.handleCartEvent)
      );
    }

    handleCartEvent = () => this.refresh();

    startPolling() {
      this.stopPolling();
      this.pollId = setInterval(() => this.refresh(), POLL_MS);
    }

    stopPolling() {
      if (this.pollId) { clearInterval(this.pollId); this.pollId = null; }
    }

    async refresh() {
      try {
        const res = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        if (!res.ok) return;
        const cart = await res.json();
        lastCart = {
          items: cart.item_count || 0,
          eligibleTotal: this.#eligibleTotal(cart.total_price || 0, cart.items || [])
        };
        this.render(lastCart);
      } catch (_) { /* mid-morph, ignore */ }
    }

    #eligibleTotal(total, items) {
      const giftValue = items
        .filter((i) => {
          const p = i.properties || {};
          return p[GIFT_PROP] === 'true' || p[GIFT_PROP] === true;
        })
        .reduce((s, i) => s + (i.final_line_price || 0), 0);
      return Math.max(0, total - giftValue);
    }

    render({ items, eligibleTotal }) {
      // ---- Free shipping status line ($ based) ----
      const shipOk = eligibleTotal >= FREE_SHIP;
      const shipEl = this.querySelector('[data-lrb-ship]');
      if (shipEl) shipEl.setAttribute('data-reached', shipOk ? 'true' : 'false');
      const shipTxt = this.querySelector('[data-lrb-ship-text]');
      if (shipTxt) {
        shipTxt.innerHTML = shipOk
          ? 'Free shipping unlocked'
          : `<strong>${money(FREE_SHIP - eligibleTotal)}</strong> away from free shipping`;
      }
      this.setAttribute('data-ship', shipOk ? 'true' : 'false');

      // ---- Discount track (item based) ----
      const reached = items < TIERS[0].items ? 0 : Math.min(items, MAX_ITEMS) - 1;
      const fillPct = (reached / TIERS.length) * 100;

      const fill = this.querySelector('[data-lrb-fill]');
      if (fill) fill.style.width = fillPct + '%';

      this.querySelectorAll('[data-lrb-dot]').forEach((dot) => {
        const need = parseInt(dot.getAttribute('data-lrb-dot'), 10);
        dot.setAttribute('data-reached', items >= need ? 'true' : 'false');
      });

      // ---- Caption (next discount tier) ----
      const captionEl = this.querySelector('[data-lrb-caption]');
      if (captionEl) captionEl.innerHTML = this.#caption(items);

      this.setAttribute('data-state', items >= MAX_ITEMS ? 'max' : 'progress');

      // ---- Celebrate a NEW unlock exactly once ----
      if (seen === null) {
        seen = { tier: reached, ship: shipOk }; // baseline on first paint, no confetti
      } else {
        const newUnlock = reached > seen.tier || (shipOk && !seen.ship);
        if (newUnlock) this.#celebrate();
        seen = { tier: reached, ship: shipOk };
      }
    }

    #celebrate() {
      window.__lrbUnlocks = (window.__lrbUnlocks || 0) + 1; // test hook
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (typeof window.lavestonConfetti === 'function') window.lavestonConfetti();
    }

    #caption(items) {
      if (items >= 5) return "Amazing — you've unlocked the full <strong>30% off</strong>!";
      if (items === 4) return "Yay! You've unlocked <strong>20%</strong>. Just drop in one more item for <strong>30%</strong>";
      if (items === 3) return "Yay! You've unlocked <strong>15%</strong>. Just drop in one more item for 20%";
      if (items === 2) return "Yay! You've unlocked <strong>10%</strong>. Just drop in one more item for 15%";
      if (items === 1) return "So close — add <strong>1 more item</strong> to unlock 10% off";
      return "Add <strong>2 items</strong> and save 10% — up to 30% off";
    }
  }

  customElements.define('laveston-rewards-bar', LavestonRewardsBar);
})();
